// §4.8 Setup matrix (`/planning/settings/setup-matrix`, P3-I-2, D-400): one
// work centre's changeover minutes, grouped by facet, read on
// `planning.calendar.read` and edited on `planning.calendar.manage`. Each
// group is a grid of from value, to value and minutes with `*` (any value) and
// `-` (the facet absent) offered beside the values the line's routings use; a
// row naming a value no active routing on the line uses carries the lint; the
// rows are checked by the kernel's own rules before a save reaches the server;
// and a test panel asks the server for the kernel's changeover between two
// keys with the rows that priced it (row 54a). Save sends the work centre's
// whole matrix with the version the screen read. A reader without the manage
// code, and every phone, reads it as cards.

import { useEffect, useMemo, useState } from "react";
import type { TFunction } from "i18next";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { Button } from "../../../../components/button";
import { Card } from "../../../../components/card";
import { DataTable, type DataTableColumn } from "../../../../components/data-table";
import { Field } from "../../../../components/field";
import { Select } from "../../../../components/select";
import { CardSkeleton, EmptyState, ErrorState } from "../../../../components/states";
import { StatusChip } from "../../../../components/status-chip";
import { Banner } from "../../../../components/toast";
import { useDesktop } from "../../../../components/use-desktop";
import { UnauthorizedError } from "../../../../lib/problem";
import { RequestError, requestIdOf } from "../../../../lib/request";
import { useCalendarWorkCenters, type WorkCenter } from "../hooks/calendar-queries";
import { usePlanningPermissions } from "../hooks/permissions";
import { problemExtra } from "../hooks/planning-queries";
import {
  putSetupMatrix,
  scheduleKeys,
  useChangeoverExplanation,
  useSetupMatrix,
  type SetupMatrix,
  type SetupMatrixRow,
} from "../hooks/schedule-queries";
import { workCenterLabel } from "../lib/calendar-lib";
import { useOffline } from "../lib/planning-lib";
import {
  ABSENT_VALUE,
  ANY_VALUE,
  FACET_RE,
  MATRIX_ROW_REASONS,
  OPAQUE_FACET,
  SETUP_MATRIX_MAX_ROWS,
  checkMatrix,
  facetOrder,
  isUnusedRow,
  minutesOf,
  sameMatrix,
  sendOrder,
  validateSetupMatrixSearch,
  type MatrixDraft,
  type MatrixRowReason,
} from "../lib/schedule-lib";
import { PlanningHeader } from "../components/planning-header";
import { usePlanningFacility } from "../planning-layout";
import { pickableWorkCenters } from "../../../../lib/work-centers";

interface Notice {
  readonly kind: "ok" | "warn" | "bad";
  readonly text: string;
}

let draftSeq = 0;
function localId(): string {
  draftSeq += 1;
  return `new-${String(draftSeq)}`;
}

function draftsOf(rows: readonly SetupMatrixRow[]): MatrixDraft[] {
  return rows.map((r) => ({
    id: r.id,
    facet: r.facet,
    from: r.from_value,
    to: r.to_value,
    minutes: String(r.minutes),
  }));
}

export function PlanningSetupMatrixScreen() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const desktop = useDesktop();
  const { facility } = usePlanningFacility();
  const perms = usePlanningPermissions();
  const holdsManage = perms.has("planning.calendar.manage");
  const canManage = holdsManage && desktop;
  const rawSearch: Record<string, unknown> = useSearch({ strict: false });
  const search = validateSetupMatrixSearch(rawSearch);
  const workCenters = useCalendarWorkCenters(facility.id);
  const active = useMemo(
    () =>
      pickableWorkCenters(workCenters.data).sort((a, b) =>
        a.code < b.code ? -1 : a.code > b.code ? 1 : 0,
      ),
    [workCenters.data],
  );
  const chosen = active.find((w) => w.id === search.wc) ?? active[0];
  const matrix = useSetupMatrix(facility.id, chosen?.id);
  const [notice, setNotice] = useState<Notice | undefined>(undefined);
  const [dirty, setDirty] = useState(false);

  useEffect(() => {
    setNotice(undefined);
    setDirty(false);
  }, [facility.id, chosen?.id]);

  const authError = [workCenters.error, matrix.error].find((e) => e instanceof UnauthorizedError);
  useEffect(() => {
    if (authError !== undefined) void navigate({ to: "/login" });
  }, [authError, navigate]);

  const head = <PlanningHeader title={t("planning.matrix.title")} />;

  if (workCenters.error !== null && authError === undefined) {
    return (
      <section className="planning-screen planning-matrix" data-testid="planning-matrix">
        {head}
        <ErrorState
          title={t("planning.matrix.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void workCenters.refetch()}
          requestId={requestIdOf(workCenters.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      </section>
    );
  }
  if (workCenters.isPending) {
    return (
      <section className="planning-screen planning-matrix" data-testid="planning-matrix">
        {head}
        <div data-testid="planning-matrix-loading">
          <CardSkeleton lines={6} label={t("planning.matrix.loading")} />
        </div>
      </section>
    );
  }
  if (chosen === undefined) {
    return (
      <section className="planning-screen planning-matrix" data-testid="planning-matrix">
        {head}
        <div data-testid="planning-matrix-no-wc">
          <EmptyState title={t("planning.matrix.noWcTitle")} body={t("planning.matrix.noWcBody")} />
        </div>
      </section>
    );
  }

  return (
    <section
      className="planning-screen planning-matrix"
      data-testid="planning-matrix"
      data-work-center-id={chosen.id}
    >
      {head}
      {!holdsManage ? (
        <p className="planning-note" data-testid="planning-matrix-read-only">
          {t("planning.matrix.readOnly")}
        </p>
      ) : null}
      {holdsManage && !desktop ? (
        <p className="planning-note" data-testid="planning-matrix-phone">
          {t("planning.matrix.phoneReadOnly")}
        </p>
      ) : null}
      <div className="planning-schedule-controls">
        <Field label={t("planning.matrix.workCenter")}>
          <Select
            value={chosen.id}
            disabled={dirty}
            data-testid="planning-matrix-wc"
            onChange={(e) => {
              void navigate({
                to: "/planning/settings/setup-matrix",
                search: { wc: e.target.value },
                replace: true,
              });
            }}
          >
            {active.map((w: WorkCenter) => (
              <option key={w.id} value={w.id}>
                {workCenterLabel(w, i18n.language)}
              </option>
            ))}
          </Select>
        </Field>
        {dirty ? <span className="planning-hint">{t("planning.matrix.saveFirst")}</span> : null}
      </div>
      {notice !== undefined ? (
        <Banner
          kind={notice.kind}
          data-testid="planning-matrix-notice"
          onDismiss={() => {
            setNotice(undefined);
          }}
          dismissLabel={t("planning.common.close")}
        >
          {notice.text}
        </Banner>
      ) : null}
      {matrix.error !== null && authError === undefined ? (
        <ErrorState
          title={t("planning.matrix.loadError")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void matrix.refetch()}
          requestId={requestIdOf(matrix.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : matrix.data === undefined ? (
        <div data-testid="planning-matrix-loading">
          <CardSkeleton lines={6} label={t("planning.matrix.loading")} />
        </div>
      ) : canManage ? (
        <MatrixEditor
          key={`${chosen.id}|${matrix.data.updated_at ?? "none"}`}
          facilityId={facility.id}
          matrix={matrix.data}
          onDirty={setDirty}
          onNotice={setNotice}
          onReload={async () => {
            await matrix.refetch();
          }}
        />
      ) : (
        <MatrixReadOnly matrix={matrix.data} />
      )}
      {matrix.data !== undefined ? (
        <TestPanel facilityId={facility.id} workCenterId={chosen.id} dirty={dirty} />
      ) : null}
    </section>
  );
}

// ---- reading ---------------------------------------------------------------------

function UnusedChip() {
  const { t } = useTranslation();
  return (
    <StatusChip variant="warn" data-testid="planning-matrix-unused">
      {t("planning.matrix.unused")}
    </StatusChip>
  );
}

function MatrixReadOnly({ matrix }: { matrix: SetupMatrix }) {
  const { t } = useTranslation();
  if (matrix.rows.length === 0) {
    return (
      <div data-testid="planning-matrix-empty">
        <EmptyState title={t("planning.matrix.emptyTitle")} body={t("planning.matrix.emptyBody")} />
      </div>
    );
  }
  const columns: DataTableColumn<SetupMatrixRow>[] = [
    {
      key: "from",
      header: t("planning.matrix.from"),
      id: true,
      render: (r) => valueText(r.from_value, t),
    },
    { key: "to", header: t("planning.matrix.to"), render: (r) => valueText(r.to_value, t) },
    {
      key: "minutes",
      header: t("planning.matrix.minutes"),
      numeric: true,
      render: (r) => r.minutes,
    },
    {
      key: "lint",
      header: t("planning.matrix.lint"),
      render: (r) => (r.unused ? <UnusedChip /> : null),
    },
  ];
  return (
    <>
      {facetOrder(matrix.rows.map((r) => r.facet)).map((facet) => (
        <Card key={facet} title={facetTitle(facet, t)} data-testid="planning-matrix-group">
          <DataTable
            columns={columns}
            rows={matrix.rows.filter((r) => r.facet === facet)}
            rowKey={(r) => r.id}
            rowTestId="planning-matrix-row"
            rowProps={(r) => ({
              "data-facet": r.facet,
              "data-from": r.from_value,
              "data-to": r.to_value,
              "data-minutes": String(r.minutes),
              "data-unused": String(r.unused),
            })}
            caption={facetTitle(facet, t)}
            empty={null}
          />
        </Card>
      ))}
    </>
  );
}

function valueText(value: string, t: TFunction): string {
  if (value === ANY_VALUE) return `* (${t("planning.matrix.any")})`;
  if (value === ABSENT_VALUE) return `- (${t("planning.matrix.absent")})`;
  return value;
}

function facetTitle(facet: string, t: TFunction): string {
  return facet === OPAQUE_FACET
    ? t("planning.matrix.opaqueFacet")
    : t("planning.matrix.facet", { facet });
}

// ---- editing ---------------------------------------------------------------------

function MatrixEditor({
  facilityId,
  matrix,
  onDirty,
  onNotice,
  onReload,
}: {
  facilityId: string;
  matrix: SetupMatrix;
  onDirty: (dirty: boolean) => void;
  onNotice: (notice: Notice | undefined) => void;
  onReload: () => Promise<void>;
}) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const offline = useOffline();
  const initial = useMemo(() => draftsOf(matrix.rows), [matrix.rows]);
  const [rows, setRows] = useState<MatrixDraft[]>(initial);
  const [extraFacets, setExtraFacets] = useState<string[]>([]);
  const [touched, setTouched] = useState<ReadonlySet<string>>(new Set());
  const [attempted, setAttempted] = useState(false);
  const [serverIssues, setServerIssues] = useState<ReadonlyMap<string, MatrixRowReason[]>>(
    new Map(),
  );
  const [newFacet, setNewFacet] = useState("");
  const [facetError, setFacetError] = useState(false);
  const [saving, setSaving] = useState(false);

  const dirty = !sameMatrix(rows, initial);
  useEffect(() => {
    onDirty(dirty);
  }, [dirty, onDirty]);

  // A value is checked and sent without the spaces around it.
  const ordered = useMemo(
    () => sendOrder(rows).map((r) => ({ ...r, from: r.from.trim(), to: r.to.trim() })),
    [rows],
  );
  const localIssues = useMemo(() => {
    const byIndex = checkMatrix(ordered);
    const byId = new Map<string, MatrixRowReason[]>();
    byIndex.forEach((reasons, index) => {
      const row = ordered[index];
      if (row !== undefined) byId.set(row.id, reasons);
    });
    return byId;
  }, [ordered]);
  const facets = facetOrder([...rows.map((r) => r.facet), ...extraFacets]);

  const update = (id: string, patch: Partial<MatrixDraft>) => {
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
    setTouched((prev) => new Set(prev).add(id));
    setServerIssues((prev) => {
      if (!prev.has(id)) return prev;
      const next = new Map(prev);
      next.delete(id);
      return next;
    });
  };
  const addRow = (facet: string) => {
    setRows((prev) => [...prev, { id: localId(), facet, from: "", to: "", minutes: "" }]);
  };
  const removeRow = (id: string) => {
    setRows((prev) => prev.filter((r) => r.id !== id));
  };
  const addFacet = () => {
    const name = newFacet.trim();
    if (name !== OPAQUE_FACET && !FACET_RE.test(name)) {
      setFacetError(true);
      return;
    }
    setFacetError(false);
    setNewFacet("");
    if (!facets.includes(name)) setExtraFacets((prev) => [...prev, name]);
    addRow(name);
  };

  const save = async () => {
    setAttempted(true);
    onNotice(undefined);
    if (localIssues.size > 0) {
      onNotice({ kind: "bad", text: t("planning.matrix.fixRows", { count: localIssues.size }) });
      return;
    }
    if (ordered.length > SETUP_MATRIX_MAX_ROWS) {
      onNotice({ kind: "bad", text: t("planning.matrix.tooMany", { max: SETUP_MATRIX_MAX_ROWS }) });
      return;
    }
    setSaving(true);
    try {
      const answer = await putSetupMatrix(matrix.work_center_id, {
        expected_updated_at: matrix.updated_at,
        rows: ordered.map((r) => ({
          facet: r.facet,
          from_value: r.from,
          to_value: r.to,
          minutes: minutesOf(r.minutes),
        })),
      });
      qc.setQueryData(scheduleKeys.matrix(facilityId, matrix.work_center_id), answer);
      onNotice({ kind: "ok", text: t("planning.matrix.saved", { count: answer.rows.length }) });
    } catch (error) {
      if (error instanceof RequestError && error.code === "SETUP_MATRIX_CHANGED") {
        onNotice({ kind: "warn", text: t("planning.common.changedBySomeone") });
        await onReload();
      } else if (error instanceof RequestError && error.code === "SETUP_MATRIX_INVALID") {
        const issues = problemExtra(error, "rows");
        const byId = new Map<string, MatrixRowReason[]>();
        if (Array.isArray(issues)) {
          for (const issue of issues as { index?: unknown; reasons?: unknown }[]) {
            const row = typeof issue.index === "number" ? ordered[issue.index] : undefined;
            const reasons = Array.isArray(issue.reasons)
              ? (issue.reasons as unknown[]).filter((r): r is MatrixRowReason =>
                  (MATRIX_ROW_REASONS as readonly unknown[]).includes(r),
                )
              : [];
            if (row !== undefined) byId.set(row.id, reasons);
          }
        }
        setServerIssues(byId);
        onNotice({ kind: "bad", text: t("planning.matrix.fixRows", { count: byId.size }) });
      } else {
        onNotice({
          kind: "bad",
          text: t("planning.common.saveFailed", { requestId: requestIdOf(error) ?? "-" }),
        });
      }
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="planning-matrix-editor" data-testid="planning-matrix-editor">
      {rows.length === 0 && extraFacets.length === 0 ? (
        <div data-testid="planning-matrix-empty">
          <EmptyState
            title={t("planning.matrix.emptyTitle")}
            body={t("planning.matrix.emptyBody")}
          />
        </div>
      ) : null}
      {facets.map((facet) => {
        const values = [ANY_VALUE, ABSENT_VALUE, ...(matrix.facets_in_use[facet] ?? [])];
        const listId = `planning-matrix-values-${facet === OPAQUE_FACET ? "opaque" : facet}`;
        return (
          <Card
            key={facet}
            title={facetTitle(facet, t)}
            data-testid="planning-matrix-group"
            action={
              <Button
                size="sm"
                data-testid="planning-matrix-add-row"
                onClick={() => {
                  addRow(facet);
                }}
              >
                {t("planning.matrix.addRow")}
              </Button>
            }
          >
            <datalist id={listId}>
              {values.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
            <div
              className="planning-matrix-grid"
              role="group"
              aria-label={facetTitle(facet, t)}
              {...{ "data-facet": facet }}
            >
              <div className="planning-matrix-grid-head" aria-hidden="true">
                <span>{t("planning.matrix.from")}</span>
                <span>{t("planning.matrix.to")}</span>
                <span>{t("planning.matrix.minutes")}</span>
                <span />
              </div>
              {rows
                .filter((r) => r.facet === facet)
                .map((row) => {
                  const shown = attempted || touched.has(row.id);
                  const reasons =
                    serverIssues.get(row.id) ?? (shown ? localIssues.get(row.id) : undefined) ?? [];
                  const unused =
                    row.from.trim().length > 0 &&
                    row.to.trim().length > 0 &&
                    isUnusedRow(
                      { facet: row.facet, from: row.from.trim(), to: row.to.trim() },
                      matrix.facets_in_use,
                    );
                  return (
                    <div
                      key={row.id}
                      className={
                        reasons.length > 0
                          ? "planning-matrix-row is-invalid"
                          : "planning-matrix-row"
                      }
                      data-testid="planning-matrix-row"
                      data-facet={row.facet}
                      data-from={row.from}
                      data-to={row.to}
                      data-minutes={row.minutes}
                      data-unused={String(unused)}
                      data-reasons={reasons.join(" ")}
                    >
                      <input
                        type="text"
                        list={listId}
                        value={row.from}
                        aria-label={t("planning.matrix.from")}
                        aria-invalid={reasons.length > 0 || undefined}
                        data-testid="planning-matrix-from"
                        onChange={(e) => {
                          update(row.id, { from: e.target.value });
                        }}
                      />
                      <input
                        type="text"
                        list={listId}
                        value={row.to}
                        aria-label={t("planning.matrix.to")}
                        aria-invalid={reasons.length > 0 || undefined}
                        data-testid="planning-matrix-to"
                        onChange={(e) => {
                          update(row.id, { to: e.target.value });
                        }}
                      />
                      <input
                        type="text"
                        inputMode="numeric"
                        value={row.minutes}
                        aria-label={t("planning.matrix.minutes")}
                        aria-invalid={reasons.includes("minutes_out_of_range") || undefined}
                        data-testid="planning-matrix-minutes"
                        onChange={(e) => {
                          update(row.id, { minutes: e.target.value });
                        }}
                      />
                      <Button
                        size="sm"
                        aria-label={t("planning.matrix.removeRow")}
                        data-testid="planning-matrix-remove"
                        onClick={() => {
                          removeRow(row.id);
                        }}
                      >
                        {t("planning.matrix.remove")}
                      </Button>
                      {unused || reasons.length > 0 ? (
                        <span className="planning-matrix-row-notes">
                          {unused ? <UnusedChip /> : null}
                          {reasons.map((reason) => (
                            <span
                              key={reason}
                              className="planning-bad"
                              data-testid="planning-matrix-reason"
                              data-reason={reason}
                            >
                              {t(`planning.matrix.reason.${reason}`)}
                            </span>
                          ))}
                        </span>
                      ) : null}
                    </div>
                  );
                })}
            </div>
          </Card>
        );
      })}
      <div className="planning-matrix-actions">
        <Field label={t("planning.matrix.newFacet")}>
          <input
            type="text"
            value={newFacet}
            aria-invalid={facetError || undefined}
            data-testid="planning-matrix-new-facet"
            onChange={(e) => {
              setNewFacet(e.target.value);
              setFacetError(false);
            }}
          />
        </Field>
        <Button
          data-testid="planning-matrix-add-facet"
          disabled={newFacet.trim().length === 0}
          onClick={addFacet}
        >
          {t("planning.matrix.addFacet")}
        </Button>
        {facetError ? (
          <span className="planning-bad" data-testid="planning-matrix-facet-error">
            {t("planning.matrix.facetInvalid")}
          </span>
        ) : null}
        <span className="planning-matrix-save">
          <Button
            disabled={!dirty || saving}
            data-testid="planning-matrix-discard"
            onClick={() => {
              setRows(initial);
              setExtraFacets([]);
              setTouched(new Set());
              setAttempted(false);
              setServerIssues(new Map());
              onNotice(undefined);
            }}
          >
            {t("planning.matrix.discard")}
          </Button>
          <Button
            variant="primary"
            busy={saving}
            busyLabel={t("planning.common.saving")}
            disabled={!dirty || offline}
            data-testid="planning-matrix-save"
            onClick={() => void save()}
          >
            {t("planning.common.save")}
          </Button>
        </span>
      </div>
      {offline ? <p className="planning-note">{t("planning.common.offlineWrites")}</p> : null}
    </div>
  );
}

// ---- the test panel --------------------------------------------------------------

function formatMinutes(text: string, locale: string): string {
  const n = Number(text);
  if (!Number.isFinite(n)) return text;
  return new Intl.NumberFormat(locale, { maximumFractionDigits: 2 }).format(n);
}

function TestPanel({
  facilityId,
  workCenterId,
  dirty,
}: {
  facilityId: string;
  workCenterId: string;
  dirty: boolean;
}) {
  const { t, i18n } = useTranslation();
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  const [staticMinutes, setStaticMinutes] = useState("");
  const [ask, setAsk] = useState<{ from: string; to: string; staticMinutes: string } | undefined>(
    undefined,
  );
  useEffect(() => {
    setAsk(undefined);
  }, [workCenterId]);
  const staticOk =
    staticMinutes.trim() === "" || /^\d{1,4}(\.\d{1,2})?$/u.test(staticMinutes.trim());
  const explain = useChangeoverExplanation(facilityId, workCenterId, ask);
  return (
    <Card title={t("planning.matrix.test.title")} data-testid="planning-matrix-test">
      <p className="planning-hint">{t("planning.matrix.test.body")}</p>
      {dirty ? <p className="planning-note">{t("planning.matrix.test.savedOnly")}</p> : null}
      <form
        className="planning-schedule-filters"
        onSubmit={(e) => {
          e.preventDefault();
          if (!staticOk) return;
          setAsk({ from: from.trim(), to: to.trim(), staticMinutes: staticMinutes.trim() });
        }}
      >
        <Field label={t("planning.matrix.test.from")}>
          <input
            type="text"
            value={from}
            data-testid="planning-matrix-test-from"
            onChange={(e) => {
              setFrom(e.target.value);
            }}
          />
        </Field>
        <Field label={t("planning.matrix.test.to")}>
          <input
            type="text"
            value={to}
            data-testid="planning-matrix-test-to"
            onChange={(e) => {
              setTo(e.target.value);
            }}
          />
        </Field>
        <Field label={t("planning.matrix.test.static")}>
          <input
            type="text"
            inputMode="decimal"
            value={staticMinutes}
            aria-invalid={!staticOk || undefined}
            data-testid="planning-matrix-test-static"
            onChange={(e) => {
              setStaticMinutes(e.target.value);
            }}
          />
        </Field>
        <Button type="submit" data-testid="planning-matrix-test-run" disabled={!staticOk}>
          {t("planning.matrix.test.run")}
        </Button>
      </form>
      {ask === undefined ? null : explain.isPending ? (
        <CardSkeleton lines={2} label={t("planning.matrix.test.running")} />
      ) : explain.error !== null || explain.data === undefined ? (
        <ErrorState
          title={t("planning.matrix.test.error")}
          retryLabel={t("planning.common.retry")}
          onRetry={() => void explain.refetch()}
          requestId={requestIdOf(explain.error)}
          requestIdLabel={t("planning.common.requestId")}
        />
      ) : (
        <div
          className="planning-matrix-result"
          data-testid="planning-matrix-test-result"
          data-minutes={explain.data.minutes}
          data-basis={explain.data.basis}
        >
          <p className="planning-matrix-result-head">
            {t("planning.matrix.test.result", {
              minutes: formatMinutes(explain.data.minutes, i18n.language),
            })}
          </p>
          <p className="planning-hint">
            {t(`planning.matrix.basis.${explain.data.basis}`, {
              from: explain.data.from_key ?? "",
            })}
          </p>
          <ul className="planning-line-list">
            {explain.data.facets.map((f) => (
              <li
                key={f.facet}
                data-testid="planning-matrix-test-facet"
                data-facet={f.facet}
                data-priced={String(f.row !== null)}
              >
                {f.row === null
                  ? t("planning.matrix.test.facetNoRow", { facet: f.facet, from: f.from, to: f.to })
                  : t("planning.matrix.test.facetRow", {
                      facet: f.facet,
                      from: f.from,
                      to: f.to,
                      rowFrom: f.row.from_value,
                      rowTo: f.row.to_value,
                      minutes: f.row.minutes,
                    })}
              </li>
            ))}
          </ul>
        </div>
      )}
    </Card>
  );
}
